<template>
  <div v-if="visible" class="fixed bottom-6 right-6 z-40">
    <button
      @click="toggleModal"
      class="rounded-full shadow-lg flex items-center justify-center cursor-pointer"
      aria-label="Help information"
    >
      <HelpInfoButtonIcon />
    </button>

    <Transition name="help-modal">
      <div
        v-if="isOpen"
        class="absolute bottom-16 right-0 w-72 max-w-[calc(100vw-3rem)] max-h-[330px] bg-[#FFFAEB] rounded-xl shadow-2xl px-6 pb-8 pt-4 flex flex-col"
      >
        <div class="flex-shrink-0">
          <div class="flex flex-col items-start gap-2 mb-1">
            <div class="flex-shrink-0">
              <HelpInfoCircleIcon />
            </div>
            <h3 class="text-lg font-semibold text-gray-900">
              <Translation
                :name="currentConfig.titleKey"
                :default="currentConfig.title"
              />
            </h3>
          </div>
        </div>

        <div class="overflow-y-auto flex-1 mt-2">
          <div class="text-sm">
            <p class="text-gray-600">
              <Translation
                :name="currentConfig.contentKey"
                :default="currentConfig.content"
              />
            </p>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import HelpInfoCircleIcon from "./icons/HelpInfoCircleIcon.vue";
import HelpInfoButtonIcon from "./icons/HelpInfoButtonIcon.vue";
import Translation from "./aft/Translation.vue";

const props = defineProps({
  wizardType: {
    type: String,
    required: true,
    validator: (value) => ["facility", "wage_calculation"].includes(value),
  },
  wizardStep: {
    type: String,
    default: null,
    validator: (value) =>
      value === null ||
      ["basic_info", "benchmark", "payroll", "preview", "addons"].includes(
        value
      ),
  },
  visible: {
    type: Boolean,
    default: true,
  },
});

// Facility wizard help configuration - single text block
const facilityWizardConfig = {
  title: "How this works",
  titleKey: "help_info.facility_wizard.title",
  content:
    "Step 1: Add Facility - Provide the necessary details about the facility, including its name and location. Step 2: Start Measuring Gaps - Begin assessing the discrepancies in your current processes to identify areas for improvement. Step 3: Get Wage Calculations Report - Generate a report that outlines the wage calculations based on the data collected.",
  contentKey: "help_info.facility_wizard.content",
};

// Wage calculation wizard help configurations - one for each step with single text block
const wageCalculationStepConfigs = {
  basic_info: {
    title: "Basic Information",
    titleKey: "help_info.wage_calculation.basic_info.title",
    content:
      "Select the facility for which you want to calculate the living wage gap and define the time period for your wage calculation analysis.",
    contentKey: "help_info.wage_calculation.basic_info.content",
  },
  benchmark: {
    title: "Living Wage Benchmark",
    titleKey: "help_info.wage_calculation.benchmark.title",
    content:
      "Choose the living wage benchmark that applies to your facility's location and verify the benchmark values to ensure they match your facility's context.",
    contentKey: "help_info.wage_calculation.benchmark.content",
  },
  payroll: {
    title: "Payroll Data",
    titleKey: "help_info.wage_calculation.payroll.title",
    content:
      "Define the job categories and number of workers in each category. Input the wages and benefits for each job category to calculate the gap.",
    contentKey: "help_info.wage_calculation.payroll.content",
  },
  preview: {
    title: "Preview Results",
    titleKey: "help_info.wage_calculation.preview.title",
    content:
      "Review the living wage gap calculations for each job category and understand the difference between current wages and the living wage benchmark.",
    contentKey: "help_info.wage_calculation.preview.content",
  },
  addons: {
    title: "Additional Options",
    titleKey: "help_info.wage_calculation.addons.title",
    content:
      "Choose additional reports and analysis options for your calculation. Finalize and submit your living wage gap calculation for reporting.",
    contentKey: "help_info.wage_calculation.addons.content",
  },
};

// Select the appropriate config based on wizard type and step
const currentConfig = computed(() => {
  if (props.wizardType === "facility") {
    return facilityWizardConfig;
  }
  // For wage calculation, use step-specific config or default to basic_info
  return (
    wageCalculationStepConfigs[props.wizardStep] ||
    wageCalculationStepConfigs.basic_info
  );
});

const isOpen = ref(false);

const toggleModal = () => {
  isOpen.value = !isOpen.value;
};

const closeModal = () => {
  isOpen.value = false;
};

// Close modal when clicking outside
const handleClickOutside = (event) => {
  if (isOpen.value && !event.target.closest(".fixed.bottom-6.right-6")) {
    closeModal();
  }
};

// Close modal on escape key
const handleEscape = (event) => {
  if (event.key === "Escape" && isOpen.value) {
    closeModal();
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  document.addEventListener("keydown", handleEscape);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("keydown", handleEscape);
});
</script>

<style scoped>
.help-modal-enter-active,
.help-modal-leave-active {
  transition: all 0.2s ease;
}

.help-modal-enter-from,
.help-modal-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.95);
}
</style>
